import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  IconButton,
  Grid,
  Paper,
  Divider,
  Chip,
  DialogContentText,
  Stack,
  Tooltip,
  Checkbox,
  Alert
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import BusinessIcon from '@mui/icons-material/Business';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import HomeIcon from '@mui/icons-material/Home';
import PersonIcon from '@mui/icons-material/Person';
import GroupIcon from '@mui/icons-material/Group';
import BlockIcon from '@mui/icons-material/Block';
import ReceiptIcon from '@mui/icons-material/Receipt';
import PrintIcon from '@mui/icons-material/Print';
import GuideButton from '../../common/guide/GuideButton';
import { ACCOUNTING_API } from '../../../config/api';
import { accountingApi } from '../../../config/apiClient';
import { useTranslation } from 'react-i18next';

// Hesap verisi arayüzü
interface AccountData {
  id: string;
  companyId?: string; // ✅ MULTI-TENANT
  companyName: string;
  groupId: string | null;
  authorizedPerson: string;
  phone: string;
  mobilePhone: string;
  email: string;
  website: string;
  postalCode: string;
  taxOffice: string;
  taxNumber: string;
  tradeRegisterNumber: string;
  country: string;
  city: string;
  district: string;
  address: string;
  accountCode: string;
  notes: string;
  createdAt: string;
  updatedAt?: string;
  balance?: number;
  isActive?: boolean;
  preventNegativeBalance?: boolean;
  riskLimitAmount?: number | null;
  branchId?: string | null;
}

// Sevk adresi arayüzü
interface ShippingAddress {
  id: string;
  country: string;
  city: string;
  district: string;
  authorizedPerson: string;
  postalCode: string;
  phone: string;
  mobilePhone: string;
  email: string;
  address: string;
}

// İşlem arayüzü
interface Transaction {
  id: string;
  accountId: string;
  accountName?: string;
  type: string;
  transactionType?: string;
  operationType?: string;
  amount: number;
  debitAmount?: number;
  creditAmount?: number;
  debitBalance?: number;
  description: string;
  documentNo?: string;
  referenceId?: string;
  referenceType?: string;
  date?: string;
  createdAt: string;
  createdBy?: string;
  isIncome: boolean;
  currency?: string;
  exchangeRate?: number;
}

interface AccountDetailProps {
  open: boolean;
  onClose: () => void;
  account: AccountData | null;
  groupName?: string;
  companyId?: string | null;
}

const AccountDetail: React.FC<AccountDetailProps> = ({ 
  open, 
  onClose, 
  account, 
  groupName,
  companyId
}) => {
  const { i18n } = useTranslation();
  const isTr = i18n.language.startsWith('tr');
  const localeCode = isTr ? 'tr-TR' : 'en-US';
  const tt = (trText: string, enText: string) => (isTr ? trText : enText);
  const decodeMojibake = (value?: string | null): string => {
    if (!value) return '';
    if (!/[ÃÂÅÄ]/.test(value)) return value;
    try {
      return decodeURIComponent(escape(value));
    } catch {
      return value;
    }
  };
  const getDisplayCountry = (country?: string | null): string => {
    const normalized = decodeMojibake(country || '').trim().toLowerCase();
    if (normalized === 'türkiye' || normalized === 'turkiye' || normalized === 'türkiye cumhuriyeti') {
      return isTr ? 'Türkiye' : 'Turkey';
    }
    return decodeMojibake(country || '');
  };

  // State tanımlamaları
  const [deactivateDialogOpen, setDeactivateDialogOpen] = useState(false);
  // ✅ Düzenleme state'leri kaldırıldı - Düzenleme farklı bir yolla yapılacak
  
  // ✅ Bakiye hesaplama kaldırıldı - Cari listesinden bakiye görülebilir
  
  // Sevk adresleri için state
  const [shippingAddresses, setShippingAddresses] = useState<ShippingAddress[]>([]);
  
  // ✅ editedAccount useEffect'i kaldırıldı - Artık düzenleme yok
  
  // Sevk adreslerini yükle
  useEffect(() => {
    if (!open || !account) return;
    
    const loadShippingAddresses = async () => {
      try {
        const numericId = parseInt(account.id, 10);
        if (isNaN(numericId)) return;
        
        const response = await accountingApi.get(ACCOUNTING_API.SHIPPING_ADDRESSES(numericId));
        
        const result = response.data;
        const addresses = result.data || result || [];
        
        setShippingAddresses(addresses.map((data: any) => ({
          id: data.id.toString(),
          country: data.country || '',
          city: data.city || '',
          district: data.district || '',
          authorizedPerson: data.authorizedPerson || '',
          postalCode: data.postalCode || '',
          phone: data.phone || '',
          mobilePhone: data.mobilePhone || '',
          email: data.email || '',
          address: data.address || ''
        })));
      } catch (error) {
        console.error('Sevk adresleri yüklenirken hata:', error);
      }
    };
    
    loadShippingAddresses();
  }, [open, account]);
  
  // ✅ Bakiye hesaplama useEffect'i kaldırıldı - Gerek yok

  // account null ise hiçbir şey render etme
  if (!account) return null;

  // Tarih formatı
  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    try {
      // ISO formatında bir tarih mi kontrol et (T ve Z içeriyor mu?)
      if (dateString.includes('T') && dateString.includes('Z')) {
        // "2025-06-03T06:26:58.262Z" formatını parçalara ayır
        const [tarihKismi, saatKismi] = dateString.split('T');
        
        // Tarih kısmını Türkçe formatına çevir (yyyy-MM-dd -> dd.MM.yyyy)
        const [yil, ay, gun] = tarihKismi.split('-');
        const formatliTarih = `${gun}.${ay}.${yil}`;
        
        // Saat kısmını milisaniye olmadan al (06:26:58.262Z -> 06:26:58)
        const formatliSaat = saatKismi.split('.')[0];
        
        return `${formatliTarih} ${formatliSaat}`;
      } else {
        // ISO formatında değilse normal tarih dönüşümü yap
        const date = new Date(dateString);
        return new Intl.DateTimeFormat(localeCode, {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        }).format(date);
      }
    } catch (error) {
      console.error('Tarih formatı hatası:', error);
      return dateString; // Hata durumunda orijinal string'i döndür
    }
  };
  
  // Para formatı
  const formatCurrency = (amount: number | null) => {
    if (amount === null) return isTr ? '0,00 ₺' : '0.00 ₺';
    return new Intl.NumberFormat(localeCode, {
      style: 'currency',
      currency: 'TRY',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  // Pasife alma işlemi - SQL API
  const handleDeactivate = async () => {
    // Genel Cari kontrolü
    if (account?.companyName === "Genel Cari") {
      console.log("Genel Cari hesabı pasife alınamaz.");
      setDeactivateDialogOpen(false);
      return;
    }
    
    if (!companyId || !account) return;
    
    try {
      // Account ID'yi sayıya çevir (SQL ID)
      const numericId = parseInt(account.id, 10);
      if (isNaN(numericId)) {
        throw new Error('Geçersiz hesap ID formatı');
      }

      console.log('🔄 Hesap pasife alınıyor (SQL ID:', numericId, ')');

      // SQL API'ye istek gönder
      await accountingApi.post(ACCOUNTING_API.ACCOUNT_DEACTIVATE(numericId));

      console.log('✅ Hesap pasife alındı (SQL)');
      
      // Modalı kapat
      setDeactivateDialogOpen(false);
      onClose();
    } catch (error) {
      console.error('Hesap pasife alınırken bir hata oluştu:', error);
      alert(`Hesap pasife alınırken hata oluştu: ${error instanceof Error ? error.message : 'Bilinmeyen hata'}`);
    }
  };

  // ✅ Tüm düzenleme fonksiyonları kaldırıldı - Düzenleme farklı bir yolla yapılacak

  // Yazdırma fonksiyonu
  const handlePrint = () => {
    if (!account) return;
    
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      alert('Popup engelleyici nedeniyle yazdırma penceresi açılamadı. Lütfen popup engelleyiciyi devre dışı bırakın.');
      return;
    }

    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <title>Cari Hesap Bilgileri</title>
        <style>
          @media print {
            @page {
              size: A4;
              margin: 10mm;
            }
            body {
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
          }
          
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }
          
          body {
            font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
            padding: 12px;
            background: #f5f5f5;
            font-size: 9pt;
          }
          
          .container {
            max-width: 100%;
            margin: 0 auto;
            background: white;
            padding: 12px;
            box-shadow: 0 2px 10px rgba(0,0,0,0.1);
          }
          
          .header {
            text-align: center;
            border-bottom: 2px solid #1976d2;
            padding-bottom: 8px;
            margin-bottom: 12px;
          }
          
          .header h1 {
            color: #1976d2;
            font-size: 16pt;
            margin-bottom: 2px;
          }
          
          .header p {
            color: #666;
            font-size: 9pt;
          }
          
          .info-section {
            margin-bottom: 10px;
          }
          
          .info-section h2 {
            color: #1976d2;
            font-size: 10pt;
            border-bottom: 1px solid #e0e0e0;
            padding-bottom: 4px;
            margin-bottom: 6px;
          }
          
          .info-grid {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 6px;
          }
          
          .info-grid-3 {
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            gap: 6px;
          }
          
          .info-item {
            padding: 4px;
            background: #f9f9f9;
            border-radius: 3px;
          }
          
          .info-item label {
            display: block;
            color: #666;
            font-size: 7.5pt;
            margin-bottom: 2px;
            font-weight: 600;
          }
          
          .info-item value {
            display: block;
            color: #333;
            font-size: 8.5pt;
            font-weight: 500;
          }
          
          .shipping-address {
            padding: 5px;
            background: #f0f7ff;
            border-left: 3px solid #1976d2;
            margin-bottom: 5px;
            border-radius: 3px;
          }
          
          .shipping-address-title {
            font-weight: bold;
            color: #1976d2;
            font-size: 8.5pt;
            margin-bottom: 3px;
          }
          
          .shipping-address-content {
            font-size: 7.5pt;
            color: #555;
            line-height: 1.3;
          }
          
          .footer {
            margin-top: 12px;
            padding-top: 8px;
            border-top: 1px solid #e0e0e0;
            text-align: center;
            color: #666;
            font-size: 7.5pt;
          }
          
          .print-date {
            color: #999;
            font-size: 7pt;
            margin-top: 4px;
          }
          
          @media print {
            body {
              background: white;
              padding: 0;
            }
            
            .container {
              box-shadow: none;
              padding: 8px;
            }
          }
        </style>
      </head>
      <body>
        <div class="container">
          <div class="header">
            <h1>CARİ HESAP BİLGİLERİ</h1>
            <p>Detaylı Cari Raporu</p>
          </div>

          <div class="info-section">
            <h2>📋 Temel Bilgiler</h2>
            <div class="info-grid">
              <div class="info-item">
                <label>Cari Adı</label>
                <value>${account.companyName}</value>
              </div>
              <div class="info-item">
                <label>Cari Kodu</label>
                <value>${account.accountCode || '-'}</value>
              </div>
              <div class="info-item">
                <label>Cari Grup</label>
                <value>${groupName || '-'}</value>
              </div>
              <div class="info-item">
                <label>Yetkili Kişi</label>
                <value>${account.authorizedPerson || '-'}</value>
              </div>
            </div>
          </div>

          <div class="info-section">
            <h2>📞 İletişim Bilgileri</h2>
            <div class="info-grid-3">
              <div class="info-item">
                <label>Cep Telefonu</label>
                <value>${account.mobilePhone || '-'}</value>
              </div>
              <div class="info-item">
                <label>Sabit Telefon</label>
                <value>${account.phone || '-'}</value>
              </div>
              <div class="info-item">
                <label>E-Posta</label>
                <value>${account.email || '-'}</value>
              </div>
            </div>
          </div>

          <div class="info-section">
            <h2>🧾 Vergi Bilgileri</h2>
            <div class="info-grid-3">
              <div class="info-item">
                <label>Vergi Dairesi</label>
                <value>${account.taxOffice || '-'}</value>
              </div>
              <div class="info-item">
                <label>Vergi Numarası</label>
                <value>${account.taxNumber || '-'}</value>
              </div>
              <div class="info-item">
                <label>Ticaret Sicil No</label>
                <value>${account.tradeRegisterNumber || '-'}</value>
              </div>
            </div>
          </div>

          <div class="info-section">
            <h2>🏠 Ana Adres</h2>
            <div class="info-grid-3">
              <div class="info-item">
                <label>İl</label>
                <value>${account.city || '-'}</value>
              </div>
              <div class="info-item">
                <label>İlçe</label>
                <value>${account.district || '-'}</value>
              </div>
              <div class="info-item">
                <label>Posta Kodu</label>
                <value>${account.postalCode || '-'}</value>
              </div>
              <div class="info-item" style="grid-column: span 3;">
                <label>Adres</label>
                <value>${account.address || '-'}</value>
              </div>
            </div>
          </div>

          ${shippingAddresses.length > 0 ? `
          <div class="info-section">
            <h2>🚚 Sevk Adresleri (${shippingAddresses.length})</h2>
            ${shippingAddresses.slice(0, 3).map((addr, index) => `
              <div class="shipping-address">
                <div class="shipping-address-title">${index + 1}. ${addr.city}, ${addr.district}</div>
                <div class="shipping-address-content">
                  ${addr.address}${addr.authorizedPerson ? ` | Yetkili: ${addr.authorizedPerson}` : ''}${addr.phone || addr.mobilePhone ? ` | Tel: ${addr.phone || addr.mobilePhone}` : ''}
                </div>
              </div>
            `).join('')}
            ${shippingAddresses.length > 3 ? `<div style="text-align: center; color: #999; font-size: 7.5pt; margin-top: 4px;">... ve ${shippingAddresses.length - 3} adres daha</div>` : ''}
          </div>
          ` : ''}

          <!-- ✅ Bakiye Detayları bölümü kaldırıldı -->

          <div class="footer">
            <p><strong>AQUASOFT</strong></p>
            <p class="print-date">Yazdırma Tarihi: ${new Date().toLocaleString('tr-TR')}</p>
          </div>
        </div>

        <script>
          window.onload = function() {
            window.print();
            window.onafterprint = function() {
              window.close();
            };
          };
        </script>
      </body>
      </html>
    `;

    printWindow.document.write(printContent);
    printWindow.document.close();
  };

  // Bilgi satırı bileşeni - Sadece gösterim için (düzenleme yok)
  const InfoRow = ({ 
    label, 
    value, 
    icon
  }: { 
    label: string; 
    value: string | number | null | undefined; 
    icon?: React.ReactNode;
  }) => {
    return (
      <Box sx={{ display: 'flex', mb: 1.5, alignItems: 'flex-start' }}>
        {icon && <Box sx={{ mr: 1, color: 'primary.main', minWidth: 28 }}>{icon}</Box>}
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="caption" color="text.secondary" display="block">
            {label}
          </Typography>
          <Typography variant="body2" fontWeight={500}>
            {typeof value === 'string' ? (decodeMojibake(value) || '-') : (value || '-')}
          </Typography>
        </Box>
      </Box>
    );
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        sx={{ '& .MuiDialog-paper': { borderRadius: 2 } }}
      >
        <DialogTitle
          sx={{
            bgcolor: 'primary.main',
            color: 'white',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <Typography variant="h6">{tt('Cari Detayları', 'Current Account Details')}</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <GuideButton guideKey="accounts.detail" color="inherit" edge="end" />
            <IconButton edge="end" color="inherit" onClick={onClose} aria-label="kapat">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          {/* Üst bilgi alanı */}
          <Box sx={{ mt: 2, mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <BusinessIcon sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
              <Box>
                <Typography variant="h5" fontWeight="bold" gutterBottom>
                  {account.companyName}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {tt('Hesap Oluşturma', 'Created At')}: {formatDate(account.createdAt)}
                </Typography>
              </Box>
            </Box>
            {/* ✅ Bakiye bilgisi kaldırıldı - Sadece Pasif Hesap durumu gösteriliyor */}
            <Box>
              {account.isActive === false && (
                <Chip 
                  icon={<BlockIcon />} 
                  label={tt('Pasif Hesap', 'Passive Account')} 
                  color="error" 
                  variant="outlined"
                />
              )}
            </Box>
          </Box>

          <Divider sx={{ mb: 3 }} />

          {/* Ana içerik */}
          <Grid container spacing={3}>
            {/* Cari Bilgiler */}
            <Grid item xs={12} md={6}>
              <Paper elevation={0} sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 1, height: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <PersonIcon sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant="subtitle1" fontWeight="bold" color="primary.main">
                    {tt('Temel Bilgiler', 'Basic Information')}
                  </Typography>
                </Box>

                <InfoRow 
                  label={tt('Cari Kodu', 'Account Code')} 
                  value={account.accountCode} 
                />
                
                <InfoRow 
                  label={tt('Cari Grup', 'Current Group')} 
                  value={groupName} 
                  icon={<GroupIcon fontSize="small" />}
                />
                
                <InfoRow 
                  label={tt('Yetkili Kişi', 'Authorized Person')} 
                  value={account.authorizedPerson} 
                  icon={<PersonIcon fontSize="small" />}
                />
                
                <InfoRow 
                  label={tt('Notlar', 'Notes')} 
                  value={account.notes}
                />

                <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                  <Checkbox
                    checked={account.preventNegativeBalance || false}
                    disabled
                    color="primary"
                  />
                  <Typography variant="body2" color="text.secondary">
                    {tt('Cari Hesap Bakiyesi Eksiye Düşemesin', 'Do not allow negative current account balance')}
                  </Typography>
                </Box>
              </Paper>
            </Grid>

            {/* İletişim Bilgileri */}
            <Grid item xs={12} md={6}>
              <Paper elevation={0} sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 1, height: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <PhoneIcon sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant="subtitle1" fontWeight="bold" color="primary.main">
                    {tt('İletişim Bilgileri', 'Contact Information')}
                  </Typography>
                </Box>

                <InfoRow 
                  label={tt('Sabit Telefon', 'Phone')} 
                  value={account.phone} 
                  icon={<PhoneIcon fontSize="small" />}
                />
                
                <InfoRow 
                  label={tt('Cep Telefonu', 'Mobile Phone')} 
                  value={account.mobilePhone} 
                  icon={<PhoneIcon fontSize="small" />}
                />
                
                <InfoRow 
                  label={tt('E-Posta', 'Email')} 
                  value={account.email}
                  icon={<EmailIcon fontSize="small" />}
                />
                
                <InfoRow 
                  label={tt('Web Sitesi', 'Website')} 
                  value={account.website} 
                />
              </Paper>
            </Grid>

            {/* Vergi Bilgileri */}
            <Grid item xs={12} md={6}>
              <Paper elevation={0} sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 1, height: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <ReceiptIcon sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant="subtitle1" fontWeight="bold" color="primary.main">
                    {tt('Vergi Bilgileri', 'Tax Information')}
                  </Typography>
                </Box>

                <InfoRow 
                  label={tt('Vergi Dairesi', 'Tax Office')} 
                  value={account.taxOffice} 
                />
                
                <InfoRow 
                  label={tt('Vergi Numarası', 'Tax Number')} 
                  value={account.taxNumber} 
                />
                
                <InfoRow 
                  label={tt('Ticaret Sicil No', 'Trade Registry No')} 
                  value={account.tradeRegisterNumber} 
                />
              </Paper>
            </Grid>

            {/* Adres Bilgileri */}
            <Grid item xs={12} md={6}>
              <Paper elevation={0} sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 1, height: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <HomeIcon sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant="subtitle1" fontWeight="bold" color="primary.main">
                    {tt('Adres Bilgileri', 'Address Information')}
                  </Typography>
                </Box>

                <InfoRow 
                  label={tt('Adres', 'Address')} 
                  value={account.address}
                  icon={<HomeIcon fontSize="small" />}
                />
                
                <InfoRow 
                  label={tt('İlçe', 'District')} 
                  value={account.district}
                />
                
                <InfoRow 
                  label={tt('İl', 'City')} 
                  value={account.city}
                />
                
                <InfoRow 
                  label={tt('Posta Kodu', 'Postal Code')} 
                  value={account.postalCode} 
                />
                
                <InfoRow 
                  label={tt('Ülke', 'Country')} 
                  value={getDisplayCountry(account.country)} 
                />
              </Paper>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Stack direction="row" spacing={1} sx={{ width: '100%', justifyContent: 'space-between' }}>
            <Button 
              variant="outlined" 
              color="primary" 
              startIcon={<PrintIcon />}
              onClick={handlePrint}
            >
              {tt('Yazdır', 'Print')}
            </Button>
            <Tooltip title={account?.companyName === "Genel Cari" ? tt('Genel Cari pasife alınamaz', 'Default current account cannot be made passive') : ""}>
              <span>
                <Button 
                  variant="outlined" 
                  color="error" 
                  startIcon={<BlockIcon />} 
                  onClick={() => setDeactivateDialogOpen(true)}
                  disabled={account?.companyName === "Genel Cari"}
                >
                  {tt('Pasife Al', 'Make Passive')}
                </Button>
              </span>
            </Tooltip>
          </Stack>
          {/* ✅ Düzenle butonu kaldırıldı - Düzenleme farklı bir yolla yapılacak */}
        </DialogActions>
      </Dialog>

      {/* Pasife Alma Onay Dialogu */}
      <Dialog
        open={deactivateDialogOpen}
        onClose={() => setDeactivateDialogOpen(false)}
      >
        <DialogTitle>{tt('Cari Hesabı Pasife Al', 'Make Current Account Passive')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {tt(`${account.companyName} hesabını pasife almak istediğinize emin misiniz? Bu işlem geri alınabilir, ancak pasif hesaplar listede görüntülenmeyecektir.`, `Are you sure you want to make ${account.companyName} passive? This action can be reverted, but passive accounts will not be shown in the list.`)}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeactivateDialogOpen(false)}>
            {tt('İptal', 'Cancel')}
          </Button>
          <Button onClick={handleDeactivate} color="error" autoFocus>
            {tt('Pasife Al', 'Make Passive')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AccountDetail; 